import { getDay, set } from "date-fns"
import { zonedTimeToUtc } from "date-fns-tz"
import useSWR from "swr"

import type { Booking, User } from "@prisma/client"
import type { OptionsForm } from "components/dashboard/OptionsForm"
import type { ReadBookingApiResponse } from "pages/api/booking/read"
import type { BusyTimeReadApiResponse } from "pages/api/busy-time/read"
import type { OptionsReadApiResponse } from "pages/api/options/read"
import type { ReadUserApiResponse } from "pages/api/user/read"

export type Availability = {
  dateTime: Date
  slots: number
  bookings?: Booking[] // if is frontend looks like Omit<Booking, "id" | "createdAt" | "updatedAt" | "orderId">[]
  busyTimes?: BusyTimeReadApiResponse["busyTimes"]
}

function useAvailability({
  start,
  end,
  userId // userId to retrieve availability for a specific user (staff member)
}: {
  start: Date
  end: Date
  userId?: User["id"]
}): {
  availability: Availability[]
  isValidating: boolean
  isError: boolean
  mutate: () => Promise<void>
  openingHours: OptionsForm["openingHours"] | undefined
  bookingInterval: OptionsForm["bookingInterval"] | undefined
  totalStaff: number
} {
  // get the required options from the db
  const {
    data: optionsData,
    error: optionsError,
    mutate: optionsMutate,
    isValidating: optionsIsValidating
  } = useSWR<OptionsReadApiResponse, Error>(
    `/api/options/read?options=openingHours,bookingInterval`
  )

  const openingHours = JSON.parse(
    optionsData?.options.find(o => o.name === "openingHours")?.value ?? "[]"
  ) as OptionsForm["openingHours"] | undefined
  const bookingInterval = JSON.parse(
    optionsData?.options.find(o => o.name === "bookingInterval")?.value ?? "[]"
  ) as OptionsForm["bookingInterval"] | undefined

  // get existing bookings for the current shown period on the calendar
  const {
    data: bookingsData,
    error: bookingsError,
    mutate: bookingsMutate,
    isValidating: bookingsIsValidating
  } = useSWR<ReadBookingApiResponse, Error>(
    `/api/booking/read?start=${start.toISOString()}&end=${end.toISOString()}${
      // if userId is specified, it is the admin area where only staff of admin roles can access. Need to include the orderItem (the api also includes the order > user) for the admin calendar
      userId ? `&include=orderItem` : "&select=startTime,endTime"
    }`
  )

  // get the busy times from the db. Include all users busy times where includeInBusy is set to true if userId is not set, otherwise get all the busy times for the specified user
  const {
    data: busyTimeData,
    error: busyTimeError,
    mutate: busyTimeMutate,
    isValidating: busyTimeIsValidating
  } = useSWR<BusyTimeReadApiResponse, Error>(
    `/api/busy-time/read?start=${start.toISOString()}&end=${end.toISOString()}${
      // if userId is specified, it is the admin area where only staff of admin roles can access. Need to include the busy time user data to display who else is busy
      userId ? `&include=user&userId=${userId}` : "&select=startTime,endTime"
    }`
  )

  // get the amount of staff members from the db by getting users that either have a role of STAFF or ADMIN. Also check that includeInBusy is true on the user
  const {
    data: staffData,
    error: staffError,
    mutate: staffMutate,
    isValidating: staffIsValidating
  } = useSWR<ReadUserApiResponse, Error>(`/api/user/read?getTotalStaff=true`)

  // get the bookings from the db response
  const bookings = bookingsData?.data || []

  // 1. Create an array of all possible dates between the activeStartDate and activeEndDate
  const datesInBetweenActiveAndEnd = []
  // make a copy of the activeStartDate so as not to mess with state
  let currentDate = start
  while (currentDate <= end) {
    datesInBetweenActiveAndEnd.push(currentDate)
    currentDate = set(currentDate, { date: currentDate.getDate() + 1 })
  }

  // 2. Reorder opening hours so that the first day is Sunday. Order like Sunday, Monday, Tuesday, Wednesday, Thursday, Friday, Saturday because they might be saved in any order. Also create empty objects containing something like sunday: { open: false, from: "", to: "" } for each day of the week where there is no saved data
  const defaultClosed = { open: false, from: "", to: "" }
  const openingHoursReordered = {
    sunday: openingHours?.sunday || defaultClosed,
    monday: openingHours?.monday || defaultClosed,
    tuesday: openingHours?.tuesday || defaultClosed,
    wednesday: openingHours?.wednesday || defaultClosed,
    thursday: openingHours?.thursday || defaultClosed,
    friday: openingHours?.friday || defaultClosed,
    saturday: openingHours?.saturday || defaultClosed
  }

  // 3. Create an array of all possible times for each day from the opening hours option
  const hoursInbetweenOpeningHours = []
  for (const day in openingHoursReordered) {
    // if the day is open
    if (openingHoursReordered[day as "monday"].open) {
      // get the from and to times. The date is saved in british time. Need to convert to UTC
      const from = zonedTimeToUtc(
        set(new Date(), {
          hours: parseInt(openingHoursReordered[day as "monday"].from),
          minutes: 0,
          seconds: 0,
          milliseconds: 0
        }),
        "Europe/London"
      )
      const to = zonedTimeToUtc(
        set(new Date(), {
          hours: parseInt(openingHoursReordered[day as "monday"].to),
          minutes: 0,
          seconds: 0,
          milliseconds: 0
        }),
        "Europe/London"
      )
      // create an array of all possible times between the from and to times
      const timesInBetween = []
      // NOTE change the below to <= to include the to time if bookings are less than an hour
      while (from < to) {
        timesInBetween.push(new Date(from))
        from.setMinutes(from.getMinutes() + (bookingInterval || 60))
      }
      // add the times to the hoursInbetweenOpeningHours array
      hoursInbetweenOpeningHours.push(timesInBetween)
    } else {
      // if the day is closed, add an empty array
      hoursInbetweenOpeningHours.push([])
    }
  }

  // 4. Create an array of all possible dates and times between the activeStartDate and activeEndDate taking the opening hours, bookings, busy times and total staff members into account
  const availability = [] as Availability[]
  for (const date of datesInBetweenActiveAndEnd) {
    // get the day of the week
    const day = getDay(date)
    // get the times for that day
    const times = hoursInbetweenOpeningHours[day]
    // if the day is open
    if (times.length) {
      // loop through the times
      for (const time of times) {
        // set the time to the date
        const dateTime = set(date, {
          hours: time.getHours(),
          minutes: time.getMinutes(),
          seconds: time.getSeconds(),
          milliseconds: time.getMilliseconds()
        })

        // construct the availability object for the dateTime. Need to:
        // 1. Use the amount of staff members to determine the total number of slots available
        // 2. Use the existing bookings to determine the number of slots taken
        // 3. Attach the bookings and busy times to the availability object so I can use them on the admin side - Need to access IDs to delete them, and display booking info
        const totalStaff = staffData?.totalStaff || 0
        const bookingsForDateTime = bookings.filter(b => {
          if (b.startTime && b.endTime) {
            return (
              dateTime >= new Date(b.startTime) &&
              // NOTE change the following to < if you want to allow bookings to start at the same time as another booking
              dateTime < new Date(b.endTime)
            )
          }
        })
        const busyTimesForDateTime =
          busyTimeData?.busyTimes.filter(
            b =>
              dateTime >= new Date(b.startTime) &&
              // NOTE change the following to < if you want to allow bookings to start at the same time as another booking
              dateTime < new Date(b.endTime)
          ) || []

        availability.push({
          dateTime,
          slots:
            totalStaff -
            bookingsForDateTime.length -
            busyTimesForDateTime.length,
          bookings: bookingsForDateTime,
          busyTimes: busyTimesForDateTime
        })
      }
    }
  }

  // 5. return the available times
  return {
    availability,
    isValidating:
      bookingsIsValidating ||
      busyTimeIsValidating ||
      optionsIsValidating ||
      staffIsValidating,
    isError:
      !!bookingsError || !!busyTimeError || !!optionsError || !!staffError,
    mutate: async () => {
      await bookingsMutate()
      await busyTimeMutate()
      await optionsMutate()
      await staffMutate()
    },
    // also return the opening hours and booking interval
    openingHours,
    bookingInterval,
    totalStaff: staffData?.totalStaff || 0
  }
}

export default useAvailability
